/**
 * Export utilities for downloading data in various formats
 */

/**
 * Download data as CSV file
 * @param {Array} data - Array of objects to export
 * @param {string} filename - Name of the file without extension
 * @param {Array} columns - Optional array of column definitions { key, label }
 */
export function exportToCSV(data, filename, columns = null) {
  if (!data || data.length === 0) {
    console.warn('No data to export');
    return;
  }

  // Determine columns from data if not provided
  const cols = columns || Object.keys(data[0]).map((key) => ({
    key,
    label: key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1'),
  }));

  // Create header row
  const header = cols.map((col) => `"${col.label}"`).join(',');

  // Create data rows
  const rows = data.map((item) =>
    cols
      .map((col) => {
        let value = item[col.key];
        if (value === null || value === undefined) {
          value = '';
        } else if (typeof value === 'object') {
          value = JSON.stringify(value);
        }
        // Escape quotes and wrap in quotes
        return `"${String(value).replace(/"/g, '""')}"`;
      })
      .join(',')
  );

  // Combine header and rows
  const csv = [header, ...rows].join('\n');

  // Download
  downloadFile(csv, `${filename}.csv`, 'text/csv;charset=utf-8;');
}

/**
 * Download data as JSON file
 * @param {Array|Object} data - Data to export
 * @param {string} filename - Name of the file without extension
 */
export function exportToJSON(data, filename) {
  const json = JSON.stringify(data, null, 2);
  downloadFile(json, `${filename}.json`, 'application/json');
}

/**
 * Helper function to trigger file download
 * @param {string} content - File content
 * @param {string} filename - Full filename with extension
 * @param {string} mimeType - MIME type of the file
 */
function downloadFile(content, filename, mimeType) {
  const blob = new Blob([content], { type: mimeType });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

/**
 * Format date for export
 * @param {string|Date} date - Date to format
 * @returns {string} Formatted date string
 */
export function formatDateForExport(date) {
  if (!date) return '';
  const d = new Date(date);
  return d.toISOString().split('T')[0];
}

/**
 * Prepare user data for export
 * @param {Array} users - Array of user objects
 * @returns {Array} Formatted data for export
 */
export function prepareUsersForExport(users) {
  return users.map((user) => ({
    name: user.name || '',
    email: user.email || '',
    status: user.is_active !== false ? 'Active' : 'Inactive',
    agentCount: user.agentCount || 0,
    joinedDate: formatDateForExport(user.created_at),
    lastActive: formatDateForExport(user.last_active),
  }));
}

/**
 * Prepare agent data for export
 * @param {Array} agents - Array of agent objects
 * @returns {Array} Formatted data for export
 */
export function prepareAgentsForExport(agents) {
  return agents.map((agent) => ({
    name: agent.name || '',
    category: agent.category || '',
    status: agent.status || 'draft',
    owner: agent.ownerEmail || '',
    sessions: agent.sessionCount || 0,
    createdDate: formatDateForExport(agent.created_at),
  }));
}

/**
 * Prepare request data for export
 * @param {Array} requests - Array of request objects
 * @returns {Array} Formatted data for export
 */
export function prepareRequestsForExport(requests) {
  return requests.map((req) => ({
    name: req.name || '',
    email: req.email || '',
    phone: req.phone || '',
    businessName: req.businessName || '',
    agentType: req.agentType || '',
    status: req.status || 'pending',
    submittedDate: formatDateForExport(req.created_at),
  }));
}

/**
 * Prepare feedback data for export
 * @param {Array} feedback - Array of feedback objects
 * @returns {Array} Formatted data for export
 */
export function prepareFeedbackForExport(feedback) {
  return feedback.map((fb) => ({
    type: fb.feedbackType || 'general',
    rating: fb.rating || 0,
    message: fb.message || '',
    userEmail: fb.userEmail || 'Anonymous',
    status: fb.status || 'new',
    submittedDate: formatDateForExport(fb.createdAt),
  }));
}
